/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    EventEmitter,
    Output,
} from '@angular/core';

import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ILogsStates, LogsStore } from '../../services/logs.store';
import { ILogListStates, LogListStore } from '../../services/log-list.store';

import './log-cinematic.component.less';

/**
 * @abstract
 * @description
 *     A base component used for showing a single entry of a log, containing common states such as loading, error,
 *     close event, and hasPrevious/hasNext log flags.
 *     Must be extended by a child component to provide a template override and the specific log data.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic',
    templateUrl: 'log-cinematic.component.html',
})
export class LogCinematicComponent {
    /**
     * Close the cinematic view.
     */
    @Output()
    public onClose = new EventEmitter<void>();

    /**
     * Indicator of whether the log list is being fetched.
     * Useful for the c-view when jumping back and forth a log entry requires page turning.
     */
    public readonly logListIsLoading$ = this.logsStore.tableIsLoading$;

    public readonly listLoadingErrored$ = this.logListStore.hasError$;

    /**
     * Indicator of whether the data is being loaded or errors have occured during the loading by
     * turning two respective observables into one.
     */
    public readonly busy$ = combineLatest([this.logListIsLoading$, this.listLoadingErrored$])
        .pipe(map(([loading, hasError]) => loading || hasError));

    constructor(
        protected readonly logsStore: LogsStore<ILogsStates>,
        protected readonly logListStore: LogListStore<ILogListStates>,
    ) {}

    /**
     * Switch to the next log, triggered by the selection bar.
     * Potentially cause next page load if the last entry of the page has been hit.
     * No guard for checking the last entry is needed here as there's already one from the selection
     * bar.
     */
    public switchToNextLog(): void {
        const { logListPaginationInfo$ } = this.logsStore;
        const logIndex$ = this.logListStore.recentlyViewedLogIndex$;

        combineLatest([
            logIndex$,
            logListPaginationInfo$,
        ])
            .pipe(take(1))
            .subscribe(([currentLogIndex, { page, pageSize }]) => {
                if (currentLogIndex < pageSize - 1) {
                    logIndex$.next(currentLogIndex + 1);
                } else {
                    // this will trigger a +1 page load
                    this.logsStore.setTableCurrentPage(page + 1);
                    logIndex$.next(0);
                }
            });
    }

    /**
     * Switch to the previous log, triggered by the selection bar.
     * Potentially cause previous page load if the first entry of the page has been hit.
     * No guard for checking the first page is needed here as there's already one from the selection
     * bar.
     */
    public switchToPreviousLog(): void {
        const { logListPaginationInfo$ } = this.logsStore;
        const logIndex$ = this.logListStore.recentlyViewedLogIndex$;

        combineLatest([
            logIndex$,
            logListPaginationInfo$,
        ])
            .pipe(take(1))
            .subscribe(([currentLogIndex, { page, pageSize }]) => {
                if (currentLogIndex > 0) {
                    logIndex$.next(currentLogIndex - 1);
                } else {
                    // this will trigger a -1 page load
                    this.logsStore.setTableCurrentPage(page - 1);
                    logIndex$.next(pageSize - 1);
                }
            });
    }

    /**
     * Called when a user exits or closes the view.
     */
    public handleClose(): void {
        this.onClose.emit();
    }
}
